import { createLeadWithTracking } from "../../shared/openaiLeadTracking.mjs";
import { useEffect, useState } from "react";
import { toast } from "react-toastify";
import axios from "axios"; // Add this import
import Cookies from "js-cookie";
import { trackLeadHybrid } from "@/lib/metaLeadTracking";
import LeadOtpVerification from "@/components/common/LeadOtpVerification";
import PredictorOtpLoginModal from "@/components/common/PredictorOtpLoginModal";

export default function CollegeForm({
  neetRank,
  domicileState,
  mccQuota,
  category,
  stateCategory,
  round,
  isLoggedIn,
  authToken,
  predictorAmount = 49,
  onFirstAccessUnlock,
  prefilledData = null,
  compactPaymentView = false,
}) {
  const LEAD_DRAFT_KEY = "collegePredictorLeadFormDraftV1";
  const [hasHydratedDraft, setHasHydratedDraft] = useState(false);
  const [formData, setFormData] = useState({
    firstName: "",
    lastName: "",
    email: "",
    phone: "",
    neetScore: "",
    leadSource: "College_Predictor_Form",
  });
  const [submitting, setSubmitting] = useState(false);
  const [showPayMessaging, setShowPayMessaging] = useState(false);
  const [isOtpVerified, setIsOtpVerified] = useState(false);
  const [showOtpLoginModal, setShowOtpLoginModal] = useState(false);
  const [pendingPaymentPhone, setPendingPaymentPhone] = useState("");
  const compactMode = showPayMessaging || compactPaymentView;

  const API_URL = process.env.NEXT_PUBLIC_API_URL;
  const FIRST_ACCESS_KEY = "collegePredictorFirstAccessSubmitUsedV3";

  useEffect(() => {
    try {
      const firstAccessUsed = localStorage.getItem(FIRST_ACCESS_KEY) === "1";
      setShowPayMessaging(firstAccessUsed);
    } catch (error) {
      setShowPayMessaging(false);
    }
  }, [FIRST_ACCESS_KEY]);

  useEffect(() => {
    try {
      const savedDraft = localStorage.getItem(LEAD_DRAFT_KEY);
      const parsedDraft = savedDraft ? JSON.parse(savedDraft) : null;

      if (prefilledData && Object.keys(prefilledData).length > 0) {
        setFormData((prev) => ({ ...prev, ...prefilledData }));
      } else if (parsedDraft && typeof parsedDraft === "object") {
        setFormData((prev) => ({ ...prev, ...parsedDraft }));
      }
    } catch (error) {
      console.error("Error loading college predictor draft:", error);
    } finally {
      setHasHydratedDraft(true);
    }
  }, [prefilledData]);

  useEffect(() => {
    if (!hasHydratedDraft) return;

    try {
      localStorage.setItem(LEAD_DRAFT_KEY, JSON.stringify(formData));
    } catch (error) {
      console.error("Error saving college predictor draft:", error);
    }
  }, [formData, hasHydratedDraft]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const ensureAuthTokenForPayment = async () => {
    if (isLoggedIn && authToken) {
      return authToken;
    }

    return null;
  };

  const continuePayment = async (phoneWithoutCountryCode, paymentAuthToken) => {
    const currentPath = window.location.pathname;
    const payResponse = await fetch(
      `${API_URL}/api/college-predictor/pay`,
      {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${paymentAuthToken}`,
        },
        body: JSON.stringify({
          amount: Number(predictorAmount) || 49,
          redirectBaseUrl: `${window.location.origin}/college-predictor-payment`,
          formData: {
            firstName: formData.firstName,
            lastName: formData.lastName,
            email: formData.email,
            phone: phoneWithoutCountryCode,
            neetScore: formData.neetScore,
          },
        }),
      }
    );

    const payResult = await payResponse.json();

    if (!payResponse.ok || !payResult?.paymentUrl) {
      throw new Error(payResult?.message || "Payment initiation failed");
    }

    localStorage.setItem("collegePredictorReturnUrl", currentPath);
    localStorage.setItem(
      "collegePredictorAfterPayFilters",
      JSON.stringify({
        neetRank,
        domicileState,
        mccQuota,
        category,
        stateCategory,
        round,
      })
    );

    toast.success("Redirecting to PhonePe...");
    window.location.href = payResult.paymentUrl;
  };

  const handleOtpLoginSuccess = async (loginData) => {
    try {
      if (!pendingPaymentPhone) {
        throw new Error("Missing payment context.");
      }

      const paymentToken = loginData?.token || Cookies.get("authToken");
      if (!paymentToken) {
        throw new Error("Authentication token missing after OTP login.");
      }

      setShowOtpLoginModal(false);
      await continuePayment(pendingPaymentPhone, paymentToken);
    } catch (error) {
      toast.error(error?.message || "Unable to continue payment.");
      setSubmitting(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (submitting) return;
    if (!compactMode && !isOtpVerified) {
      toast.error("Please verify your phone via WhatsApp OTP before submitting.");
      return;
    }

    setSubmitting(true);

    try {
      // Remove +91 before submitting
      const phoneWithoutCountryCode = formData.phone
        .replace(/[^0-9]/g, "")
        .slice(-10); // Only keep the last 10 digits

      // Check phone number before form submission
      const CRM_URL = process.env.NEXT_PUBLIC_ADMIN_URL;
      const phoneCheckResponse = await fetch(
        `${CRM_URL}/api/find-users-leads`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            phone: phoneWithoutCountryCode, // Send only the 10-digit phone number
            leadSource: formData.leadSource,
          }),
        }
      );

      const phoneCheckData = await phoneCheckResponse.json();

      // If phone already exists, continue to payment and skip duplicate lead creation.
      if (phoneCheckResponse.status === 200) {
        toast.info("Your details are already present.");
      }

      // If status code is 403 (phone number does not exist), create lead first.
      if (phoneCheckResponse.status === 403) {
        const name = `${formData.firstName} ${formData.lastName}`.trim();

        await createLeadWithTracking(`${process.env.NEXT_PUBLIC_ADMIN_URL}/api/leads`, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            name,
            email: formData.email,
            phone: phoneWithoutCountryCode, // Submit only the 10-digit phone number
            leadSource: formData.leadSource,
            neetScore: formData.neetScore,
            neetRank: Number(neetRank),
            domicile: domicileState,
            mcc_quota: mccQuota,
            category,
            state_category: stateCategory,
            round,
          }),
        });
        // stop syanamte submission
        // await axios.post(
        //   `${process.env.NEXT_PUBLIC_API_URL}/api/contacts/upsert`,
        //   {
        //     firstName: formData.firstName,
        //     lastName: formData.lastName,
        //     email: formData.email,
        //     phone: "+91" + formData.phone,
        //     tags: formData.leadSource || "College_Predictor_Form", // Always send tags
        //     neetScore: formData.neetScore,
        //     neetRank: Number(neetRank),
        //     domicile: domicileState,
        //     mcc_quota: mccQuota,
        //     category,
        //     state_category: stateCategory,
        //     round,
        //   }
        // );

        await trackLeadHybrid({
          email: formData.email,
          phone: phoneWithoutCountryCode,
          value: 49,
          currency: "INR",
          eventSourceUrl: "/thank-you",
          dedupeKey: `${formData.leadSource}_${phoneWithoutCountryCode}_${formData.email}`,
          customData: {
            lead_source: formData.leadSource,
            neet_score: formData.neetScore,
            neet_rank: Number(neetRank),
          },
        });
      } else if (phoneCheckResponse.status !== 200) {
        throw new Error(phoneCheckData?.message || "Phone verification failed");
      }

      // First successful submit gets one-time free access and should not go to payment.
      const firstAccessUsed = localStorage.getItem(FIRST_ACCESS_KEY) === "1";
      if (!firstAccessUsed) {
        localStorage.setItem(FIRST_ACCESS_KEY, "1");
        toast.success("First-time access unlocked. Showing your results.");
        setShowPayMessaging(true);

        if (typeof onFirstAccessUnlock === "function") {
          onFirstAccessUnlock();
        }

        setSubmitting(false);
        return;
      }

      // Payment path requires an auth token. If missing, auto-create/login from phone.
      const paymentAuthToken = await ensureAuthTokenForPayment();

      if (!paymentAuthToken) {
        toast.info("Please login with OTP to continue payment.");
        setPendingPaymentPhone(phoneWithoutCountryCode);
        setShowOtpLoginModal(true);
        setSubmitting(false);
        return;
      }

      await continuePayment(phoneWithoutCountryCode, paymentAuthToken);
    } catch (error) {
      console.error("Form Submit Error:", error);
      toast.error(error?.message || "Error submitting form. Please try again.");
      setSubmitting(false); // Reset submitting state if there's an error
    }
  };

  return (
    <>
      <div className="bg-white w-full max-w-2xl mx-auto rounded-xl p-6 sm:p-6 shadow-md border border-[#ff9245] border-xl sm:px-12">
        <h2 className="text-center text-lg sm:text-xl font-semibold text-[#1A374D] m-4 sm:mb-4">
          {compactMode
            ? `Pay ₹${Number(predictorAmount) || 49} & Continue`
            : showPayMessaging
            ? "Fill Your Details to Get Your College"
            : "Fill your details to get Your rank"}
        </h2>
        {showPayMessaging && !compactMode && (
          <p className="text-center text-sm sm:text-base font-medium text-[#0e605b] mb-6">
            Complete the form below and pay <span className="font-bold">₹{Number(predictorAmount) || 49}</span> to unlock your predictor access.
          </p>
        )}
        <form onSubmit={handleSubmit} className="space-y-3">
          {!compactMode && (
            <>
              <div className="flex flex-col sm:flex-row sm:space-x-2 space-y-2 sm:space-y-0">
                <input
                  name="firstName"
                  placeholder="First Name"
                  className="flex-1 p-2 border rounded text-[#000]"
                  value={formData.firstName}
                  onChange={handleChange}
                  required
                />
                <input
                  name="lastName"
                  placeholder="Last Name"
                  className="flex-1 p-2 border rounded text-[#000]"
                  value={formData.lastName}
                  onChange={handleChange}
                  required
                />
              </div>
              <input
                name="email"
                type="email"
                placeholder="Email"
                className="w-full p-2 border rounded text-[#000]"
                value={formData.email}
                onChange={handleChange}
                required
              />
              <LeadOtpVerification
                phone={formData.phone}
                otpEnabled={false}
                onPhoneChange={(value) => setFormData((prev) => ({ ...prev, phone: value }))}
                inlinePhoneField
                countryCodePrefix="+91"
                phonePlaceholder="Phone Number"
                onVerifiedChange={setIsOtpVerified}
              />
              <input
                name="neetScore"
                type="number"
                placeholder="neetScore"
                className="w-full p-2 border rounded text-[#000]"
                value={formData.neetScore}
                onChange={handleChange}
                required
              />
            </>
          )}
          <div className="flex items-center justify-center">
            <button
              type="submit"
              className="w-full sm:w-auto px-6 bg-orange-400 text-white py-2 rounded-3xl hover:bg-[#fff] hover:ring-1 hover:ring-[#ff9245] hover:!text-[#ff9245] transition duration-300 ease-in-out text-base sm:text-lg"
              disabled={submitting}
            >
              {submitting
                ? "Processing..."
                : showPayMessaging
                  ? `➜ Pay ₹${Number(predictorAmount) || 49} & Continue`
                  : "➜ Submit & Continue"}
            </button>
          </div>
        </form>
      </div>
      <PredictorOtpLoginModal
        isOpen={showOtpLoginModal}
        mobile={pendingPaymentPhone}
        onClose={() => {
          setShowOtpLoginModal(false);
          setSubmitting(false);
        }}
        onSuccess={handleOtpLoginSuccess}
      />
    </>
  );
}
